import { computed, reactive, shallowRef } from 'vue'
import { useRouter } from 'vue-router'
import { useBreadcrumb, useRoute, useStore } from '@/plugins'
import {
  documentAgreementContractorCreate,
  documentAgreementContractorDelete,
  documentAgreementContractors,
  documentAgreementDelete,
  documentAgreementRepeatSendApproved,
  documentAgreementSingle,
  documentAgreementUpdate,
} from '@/requests/documentAgreement'
import { cachedObject } from 'best-modules/utils'
import { DocumentAgreement } from '@/utils/types/documentAgreement'
import { DocumentAgreementCredentials } from '../types'
import { handleAsync, confirm, setSnackbar } from 'best-modules/plugins'
import { openDialog } from '@/dialog'
import { useCan } from '@/composables/useCan'
import { documentAgreementSingleChannel, socket } from '@/socket'

export function useDocumentAgreementEntity() {
  const route = useRoute()
  const router = useRouter()
  const store = useStore()
  const breadcrumb = useBreadcrumb()
  const { can } = useCan()

  const userId = computed<number>(() => store.state.user.userData?.id)
  const documentAgreementId = computed<number>(() => Number(route.params.id))

  const documentAgreementSource = reactive<DocumentAgreement>(
    {} as DocumentAgreement
  )
  const contractors = shallowRef<any[]>([])

  const documentAgreementCredentials =
    cachedObject<DocumentAgreementCredentials>({
      name: null,
      number: null,
      contractors: [] as any[],
    })

  const setDocumentAgreement = (obj: Partial<DocumentAgreement>) => {
    Object.assign(documentAgreementSource, obj)
  }

  const setCredentials = () => {
    documentAgreementCredentials.$set({
      name: documentAgreementSource.name ?? null,
      number: documentAgreementSource.number ?? null,
      contractors: contractors.value.map(c => ({
        documentAgreementContractorId: c.id,
        id: c.contractor.id,
        name: c.contractor.name,
        code: c.contractor.code,
      })),
    })
  }

  const getDocumentAgreement = () => {
    return documentAgreementSingle(documentAgreementId.value).then(res => {
      setDocumentAgreement(res)

      breadcrumb.set([
        {
          title: 'Договори',
          to: { name: 'document-agreement-list' },
          index: 0,
        },
        { title: documentAgreementSource.name, index: 1 },
      ])
    })
  }

  const getContractors = () => {
    return documentAgreementContractors(documentAgreementId.value).then(res => {
      contractors.value = res
    })
  }

  const isAuthor = computed(
    () => documentAgreementSource.authorId === userId.value
  )
  const isErm = computed(() => !!documentAgreementSource.ermId)
  const readonly = computed(
    () =>
      !can('document-agreements.update') ||
      !isAuthor.value ||
      [3, 4].includes(documentAgreementSource.statusId)
  )

  const addContractor = () => {
    if (
      !documentAgreementCredentials.contractors.find(
        c => !c.documentAgreementContractorId
      )
    ) {
      documentAgreementCredentials.contractors.push({
        documentAgreementContractorId: null,
        id: null,
        name: null,
        code: null,
      })
    }
  }

  const removeContractor = (index: number) => {
    documentAgreementCredentials.contractors.splice(index, 1)
  }

  const setContractor = (
    index: number,
    contractor: { id: number; name: string; code: string }
  ) => {
    const alreadyAdded = documentAgreementCredentials.contractors.some(
      (c, i) => i !== index && c.id === contractor.id
    )
    if (alreadyAdded) return

    documentAgreementCredentials.contractors[index] = {
      documentAgreementContractorId: null,
      id: contractor.id,
      name: contractor.name,
      code: contractor.code,
    }
  }

  const cancelEdit = () => {
    documentAgreementCredentials.$reset()
  }

  const updateDocumentAgreement = () => {
    return handleAsync('updateDocumentAgreement', async () => {
      await documentAgreementUpdate(documentAgreementId.value, {
        name: documentAgreementCredentials.name,
        number: documentAgreementCredentials.number,
      })

      const current = documentAgreementCredentials.contractors.filter(c => c.id)
      const cached = documentAgreementCredentials.$cache.contractors || []
      const added = current.filter(c => !c.documentAgreementContractorId)
      const removed = cached.filter(
        cc =>
          cc.documentAgreementContractorId &&
          !current.some(
            c =>
              c.documentAgreementContractorId ===
              cc.documentAgreementContractorId
          )
      )

      await Promise.all([
        ...added.map(c =>
          documentAgreementContractorCreate({
            contractorId: c.id,
            documentId: documentAgreementId.value,
          })
        ),
        ...removed.map(c =>
          documentAgreementContractorDelete(c.documentAgreementContractorId)
        ),
      ])

      await Promise.all([getDocumentAgreement(), getContractors()])
      setCredentials()
      setSnackbar({ text: 'Документ оновлено' })
    })
  }

  const deleteDocument = () => {
    can('document-agreements.delete', () => {
      confirm({
        text: 'Ви впевненні що хочете видалити документ на завжди?',
      }).then(() => {
        return handleAsync('deleteDocument', () => {
          return documentAgreementDelete(documentAgreementId.value).then(() => {
            router.push({ name: 'document-agreement-new' })
          })
        })
      })
    })
  }

  const sendToSign = () => {
    can('document-agreements.convertToSignature', () => {
      openDialog({
        name: 'DocumentSendToSign',
        action: 'create',
        dialogData: { documentId: documentAgreementId.value },
      })
    })
  }

  const repeatSendToToErm = () => {
    return handleAsync('repeatSendToToErm', () => {
      return documentAgreementRepeatSendApproved(
        documentAgreementId.value
      ).then(() => {
        setSnackbar({ text: 'Документ успішно відправлено в ERM' })
      })
    })
  }

  const subscribeToSocket = () => {
    socket.on(documentAgreementSingleChannel, res => {
      if (res.id === documentAgreementId.value) {
        setDocumentAgreement(res)
      }
    })
  }

  return {
    documentAgreementId,
    documentAgreementSource,
    documentAgreementCredentials,
    contractors,
    getDocumentAgreement,
    getContractors,
    setDocumentAgreement,
    setCredentials,
    isAuthor,
    isErm,
    readonly,
    userId,
    addContractor,
    removeContractor,
    setContractor,
    updateDocumentAgreement,
    cancelEdit,
    deleteDocument,
    sendToSign,
    repeatSendToToErm,
    subscribeToSocket,
  }
}
